import store from '@/vuex/store'
import { isBulkSlideAddSupported } from '@/services/office/insert-slides'
import { canReadSlideTags } from '@/services/office/slide-tags'
import { isPptOnlineHost } from '@/utils/ppt-online.utils'

// AHA-46329: the multi-slide selector needs the flag AND a PowerPoint that can do both halves
// of the flow. An unsupported host (iPad, volume-licensed perpetual Office incl. 2024 LTSC)
// must never reach the selector, because the Add button there could not do anything — so those
// users silently keep the original flow.
//
// The two capabilities are checked separately because they are two different dependencies:
//   - exportAsBase64 (1.8) — the donor export the clone trick depends on, i.e. can we INSERT?
//   - TagCollection (1.3) — the per-slide binding every inserted slide gets, i.e. can the
//     inserted slides be TOLD APART afterwards? An insert whose slides cannot be tagged is
//     worse than no insert: the clones all inherit one binding and render the same slide.
// On a spec-compliant host requirement sets are monotonic, so 1.8 already implies 1.3 and the
// second check never changes the answer. It is here so the gate states what it actually needs
// instead of leaning on that arithmetic — and so a host that reports the sets non-monotonically
// (or a shimmed/partial Office.js) fails the gate rather than reaching a half-working selector.
//
// This is its own module on purpose: services/office/index.js needs it, and importing it
// from ppt-select-presentation.js (which imports @/services/office) would be circular.
//
// AHA-43111: PowerPoint for the web is excluded outright, ahead of the capability probes. The
// probes ask what the Office.js API surface offers; the web's problem is underneath that. Its
// add-in frames all load one URL and share document.settings live, so a slide inserted there
// cannot be reliably told apart from its donor — the exact failure the tags above exist to
// prevent, and one no requirement-set check reports, because the API is present and answers.
// The web host would therefore pass both probes and reach a selector whose inserts land on the
// wrong slides. Desktop (PC/Mac) is unaffected and keeps the full flow.
//
// Every caller of this gate already has a working closed-gate path, so this narrowing adds no
// new branches anywhere — it routes the web onto the pre-AHA-46329 behaviour: "Select
// presentation" binds the whole presentation, the list row goes inert, a frame that lands on
// the selector URL is bounced to the presentation list, and a blank frame cold-loads into the
// editor instead of the selector.
export const canUsePptSlideSelector = () => {
  if (isPptOnlineHost()) return false
  return Boolean(store.getters['featureToggles/canUsePptMultipleSelect'])
    && isBulkSlideAddSupported()
    && canReadSlideTags()
}

// AHA-46754: same host requirement, plus its own flag, for the ROW-BODY route into the
// selector — a click on a list row anywhere other than its "Select presentation" button.
// The getter already ANDs in AHA-46329, so this stays a strict subset of
// canUsePptSlideSelector(): with AHA-46754 off the button keeps opening the selector and
// the rest of the row goes back to what it did before AHA-46329 (inert row; thumbnail and
// name cells open the editor).
//
// AHA-43111: the web exclusion has to be repeated here, not inherited. This function does not
// call canUsePptSlideSelector() — it reproduces its host check independently — so without this
// line "strict subset" would stop being true on the web: the row would still open a selector
// that canUsePptSlideSelector() then bounces straight back to the list on mount, i.e. a click
// that visibly does nothing.
export const canUsePptRowClickSelector = () => {
  if (isPptOnlineHost()) return false
  return Boolean(store.getters['featureToggles/canUsePptPrezListRowClick']) && isBulkSlideAddSupported()
}
